import { useEffect, useMemo, useRef } from "react";

// Simple throttle that always calls the latest handler
const useThrottled = (fn, delay) => {
  const fnRef = useRef(fn);
  const lastCallRef = useRef(0);
  const timeoutRef = useRef(null);

  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);

  const throttled = useMemo(() => {
    const t = (...args) => {
      const now = Date.now();
      const remaining = delay - (now - lastCallRef.current);

      const call = () => {
        lastCallRef.current = Date.now();
        fnRef.current(...args);
      };

      if (remaining <= 0) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        call();
      } else if (!timeoutRef.current) {
        timeoutRef.current = setTimeout(() => {
          timeoutRef.current = null;
          call();
        }, remaining);
      }
    };
    t.cancel = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
    return t;
  }, [delay]);

  useEffect(() => () => throttled.cancel(), [throttled]);

  return throttled;
};

/**
 * Invoke handler when an event occurs outside the given element.
 * @param {import('react').RefObject<HTMLElement>} ref
 * @param {boolean} isActive
 * @param {() => void} handler
 * @param {{
 *  events?: string[],
 *  throttleMs?: number,
 *  target?: EventTarget,
 *  capture?: boolean,
 *  passive?: boolean
 * }} [opts]
 */
export function useOutsideEvent(ref, isActive, handler, opts = {}) {
  const {
    events = ["pointerdown", "pointermove"],
    throttleMs = 100,
    target,
    capture = true,
    passive = true,
  } = opts;

  const throttled = useThrottled(handler, throttleMs);

  // Extract eventsKey for stable dependency tracking
  const eventsKey = events.join("|");

  useEffect(() => {
    // SSR/No DOM guard
    if (typeof window === "undefined") return;
    const el = ref?.current;
    const t = target ?? document;
    if (!t || !isActive) return;

    const listener = (event) => {
      // If ref not mounted, treat as outside
      const inside = el && el.contains && el.contains(event.target);
      if (!inside) throttled(event);
    };

    const options = { capture, passive };
    events.forEach((ev) => t.addEventListener(ev, listener, options));

    return () => {
      events.forEach((ev) => t.removeEventListener(ev, listener, options));
      throttled.cancel();
    };
  }, [ref, isActive, target, capture, passive, events, eventsKey, throttled]);
}
